/**
 * IntegrationMappingPanel — shared field mapping panel for all integrations.
 *
 * Replaces the near-identical ShopifyPanel, AccelaPanel, and any future
 * integration panel. The only integration-specific values are passed as props:
 *   - fieldKey        the raw API key name ("ShopifyField", "accelaField", etc.)
 *   - fieldLabel      column header shown to admins ("Shopify Field", etc.)
 *   - apiPrefix       URL segment ("Shopify/Integrations", "Accela/Integrations")
 *   - getFieldsPath   endpoint for fetching available fields ("GetShopifyFields")
 *   - scopeOptions    list of scope/record type strings for the selector
 *
 * Internal state always uses the normalized `IntegrationFieldMap.field` key.
 * Serialization to/from the API maps `field` ↔ `fieldKey` transparently.
 */

import useApi from "@/hooks/useApi";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { SingleValue } from "react-select";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import CustomSelect from "../CustomSelect";
// Note: AlertTriangle/IconClose icons removed — replaced by ds button/modal styles.
import IntegrationMappingRow, { Option } from "./IntegrationMappingRow";
import { ChevronDown, HelpCircle } from "@/components/ds/icons";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton,
} from "@/components/ds";

// ---------------------------------------------------------------------------
// Column header help — always-accessible replacement for hover-only `title=`
// tooltips. The icon's accessible name (aria-label) is exposed to assistive
// tech and keyboard focus regardless of hover/touch support; `title` is kept
// as a bonus desktop hover hint, never as the sole explanation.
// ---------------------------------------------------------------------------

function ColumnHelp({ description }: { description: string }) {
  return (
    <button
      type="button"
      tabIndex={0}
      aria-label={description}
      title={description}
      className="inline-flex align-middle ml-1 text-zinc-400 hover:text-zinc-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 rounded-full"
    >
      <HelpCircle className="w-3.5 h-3.5" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Normalized mapping row — all integrations use this internally. */
export interface IntegrationFieldMap {
  id: number;
  /** Normalized: the selected integration field as a react-select Option. */
  field: SingleValue<Option>;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IntegrationMappingPanelProps {
  /** Panel starts with this scope type selected. */
  scopeType: string;
  /** The config ID for this integration instance. */
  selectedConfigId: number;
  /** Needed to resolve the config list hook internally if used; pass 0 if unused. */
  teamId: number;
  /** Callback to surface "All changes saved" indicator in the parent. */
  showSavedMessage: () => void;
  /** Disable the "Delete Record" button when this is the only panel. */
  isDeleteDisabled: boolean;

  // -- Integration-specific config --

  /** Raw key name sent to/received from the API, e.g. "ShopifyField". */
  fieldKey: string;
  /** Column header, e.g. "Shopify Field". */
  fieldLabel: string;
  /** URL prefix for all API calls, e.g. "Shopify/Integrations". */
  apiPrefix: string;
  /** Path used to fetch available fields, e.g. "GetShopifyFields". */
  getFieldsPath: string;
  /** Scope / record type options shown in the panel header selector. */
  scopeOptions: string[];
  /** Optional live filter — only rows whose field name matches are shown. */
  filterText?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IntegrationMappingPanel(props: IntegrationMappingPanelProps) {
  const {
    fieldKey,
    fieldLabel,
    apiPrefix,
    getFieldsPath,
    scopeOptions: scopeOptionsList,
    selectedConfigId,
    showSavedMessage,
    isDeleteDisabled,
    filterText = ""
  } = props;

  const api = useApi();

  const [mappedFields, setMappedFields] = useState<IntegrationFieldMap[]>([]);
  const [scopeType, setScopeType] = useState<string>(props.scopeType);
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Scope-level delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scopeTypeToDelete, setScopeTypeToDelete] = useState<string | null>(null);

  // Field-level delete modal
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ index: number; fieldName: string } | null>(null);

  // Guard against auto-save on initial render
  const isInitialRender = useRef(true);

  // ── fetch available fields for the current scope ──────────────────────────
  useEffect(() => {
    async function fetchMetadata() {
      if (!scopeType || selectedConfigId == null) {
        setMetadata([]);
        return;
      }
      setIsLoading(true);
      try {
        const resp = await api.messaging.post(
          `${apiPrefix}/${getFieldsPath}/${selectedConfigId}`,
          scopeType
        );
        const json = await resp.json();
        const newOptions = Object.values(json).map((key) => ({
          value: key,
          label: key as string
        })) as Option[];
        setMetadata(newOptions);
      } catch (err) {
        console.error(`${fieldLabel}: failed to load fields`, err);
        setMetadata([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchMetadata();
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, getFieldsPath, fieldLabel]);

  // ── fetch saved mappings for the current scope ────────────────────────────
  const fetchMapping = useCallback(async () => {
    if (selectedConfigId == null || !scopeType) return;
    try {
      const resp = await api.messaging.get(
        `${apiPrefix}/Mapping/${scopeType}/${selectedConfigId}`
      );
      if (!resp.ok) {
        console.error(`${fieldLabel}: failed to load mapping`, resp.statusText);
        return;
      }
      const json = await resp.json();
      // Normalize: API key → internal `field` property
      const normalized: IntegrationFieldMap[] = json.map((el: any) => ({
        ...el,
        field: el[fieldKey] != null
          ? { value: el[fieldKey], label: el[fieldKey] }
          : { value: "", label: "" }
      }));
      setMappedFields(normalized);
    } catch (err) {
      console.error(`${fieldLabel}: error loading mapping`, err);
      setMappedFields([]);
    }
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, fieldKey, fieldLabel]);

  useEffect(() => {
    if (!scopeType) return;
    fetchMapping();
  }, [fetchMapping, scopeType]);

  // ── auto-save on every change (skip initial render) ───────────────────────
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    saveRecord();
  }, [mappedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── save all rows ─────────────────────────────────────────────────────────
  async function saveRecord() {
    if (selectedConfigId == null) return;
    try {
      const payload = mappedFields.map((el) => ({
        [fieldKey]: el.field?.value ?? "",
        id: el.id,
        create: el.create,
        usedForSearching: el.usedForSearching,
        showWhenSelected: el.showWhenSelected,
        showInSearch: el.showInSearch,
        defaultValue: el.defaultValue,
        scopeType: el.scopeType
      }));
      await api.messaging.post(
        `${apiPrefix}/Mapping/${selectedConfigId}`,
        { Mappings: payload }
      );
    } catch (err) {
      console.error(`${fieldLabel}: save failed`, err);
    }
  }

  // ── delete a single mapping row ───────────────────────────────────────────
  async function deleteRecord(id: number) {
    try {
      await api.messaging.delete(`${apiPrefix}/Mapping/${id}`);
    } catch (err) {
      console.error(`${fieldLabel}: delete failed`, err);
    }
  }

  // ── add a new empty row ───────────────────────────────────────────────────
  const addRow = async () => {
    const newMap: IntegrationFieldMap = {
      id: -1,
      field: { value: "", label: "" },
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      defaultValue: "",
      scopeType
    };
    setMappedFields((prev) => [...prev, newMap]);

    // Fetch real DB id for the new row after a brief delay
    setTimeout(async () => {
      try {
        const resp = await api.messaging.get(`${apiPrefix}/Mapping/`);
        const lastId = await resp.json();
        setMappedFields((prev) =>
          prev.map((f) => (f.id === -1 ? { ...f, id: lastId } : f))
        );
      } catch (err) {
        console.error(`${fieldLabel}: failed to fetch new row id`, err);
      }
    }, 1000);
  };

  // ── update a single row in state ──────────────────────────────────────────
  const updateMappedField = (index: number, mappedField: IntegrationFieldMap) => {
    setMappedFields((prev) => {
      const clone = [...prev];
      clone[index] = mappedField;
      return clone;
    });
  };

  // ── field-level delete ────────────────────────────────────────────────────
  const handleDeleteFieldClick = (index: number) => {
    const fieldName = mappedFields[index]?.field?.label || "this field";
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;
    const { index } = fieldToDelete;
    if (mappedFields[index]?.id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    setMappedFields((prev) => {
      const clone = [...prev];
      clone.splice(index, 1);
      return clone;
    });
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  // ── scope-level delete ────────────────────────────────────────────────────
  const handleDeleteRecordClick = (scope: string) => {
    setScopeTypeToDelete(scope);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!scopeTypeToDelete) return;
    try {
      await api.messaging.delete(
        `${apiPrefix}/Mapping/DeleteRecord/${scopeTypeToDelete}`
      );
      setMappedFields((prev) =>
        prev.filter((f) => f.scopeType !== scopeTypeToDelete)
      );
    } catch (err) {
      console.error(`${fieldLabel}: delete record failed`, err);
    }
    setIsDeleteModalOpen(false);
    setScopeTypeToDelete(null);
  };

  // ── render ────────────────────────────────────────────────────────────────
  const chevronClass = `w-4 h-4 transition-transform${expanded ? " rotate-180" : ""}`;

  return (
    <SectionCard
      action={
        <IconButton
          aria-label={expanded ? "Collapse panel" : "Expand panel"}
          onClick={() => setExpanded((s) => !s)}
        >
          <ChevronDown className={chevronClass} />
        </IconButton>
      }
    >
      <div className="mb-4" onClick={(e) => e.stopPropagation()}>
        <CustomSelect
          options={scopeOptionsList}
          value={scopeType}
          width="w-72"
          onChange={setScopeType}
        />
      </div>
      {expanded && (
        <div>
          <p className="mb-4 text-xs text-zinc-500 leading-relaxed border-l-2 border-zinc-300 pl-3">
            Field mappings tell Velaro how to recognize and display customers from your CRM.
            Mark at least two <strong className="text-zinc-700">Search Key</strong> fields — for example Email and Phone — so contacts who share one value can still be matched reliably during bot conversations and agent searches.
            <strong className="text-zinc-700"> In Results</strong> fields appear when multiple matches are found (both in bot disambiguation and agent search).
            <strong className="text-zinc-700"> Info Panel</strong> fields populate the agent sidebar once a contact is confirmed.
            <strong className="text-zinc-700"> Write Back</strong> fields sync data the bot collects back to your CRM.
          </p>
          <table className="p-4 w-full">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center text-zinc-700">{fieldLabel}</th>
                <th className="pb-4 p-2 w-80 text-center text-zinc-700">Default value</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Write Back
                  <ColumnHelp description="When bot collects this value, write it back to this field in the CRM" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Search Key
                  <ColumnHelp description="Use this field as a key when searching for customer records" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Info Panel
                  <ColumnHelp description="Show this field in the agent info panel when a customer record is matched" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  In Results
                  <ColumnHelp description="Show this field in the search results list" />
                </th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields
                .map((mappedField, i) => ({ mappedField, i }))
                .filter(
                  ({ mappedField }) =>
                    !filterText ||
                    (mappedField.field?.label || "")
                      .toLowerCase()
                      .includes(filterText.toLowerCase())
                )
                .map(({ mappedField, i }) => (
                  <IntegrationMappingRow
                    key={i}
                    mappedField={mappedField}
                    i={i}
                    fieldLabel={fieldLabel}
                    updateMappedField={updateMappedField}
                    isLoading={isLoading}
                    deleteRow={handleDeleteFieldClick}
                    metadata={metadata}
                    showSavedMessage={showSavedMessage}
                  />
                ))}
              {mappedFields.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-xs text-zinc-400 italic">
                    No fields mapped yet. Add a field to enable customer lookup and populate the agent info panel.
                  </td>
                </tr>
              )}
              {mappedFields.length > 0 &&
                filterText &&
                !mappedFields.some((f) =>
                  (f.field?.label || "").toLowerCase().includes(filterText.toLowerCase())
                ) && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-xs text-zinc-400 italic">
                      No fields match "{filterText}".
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {scopeType && (
            <div className="flex justify-end pt-4 gap-3">
              <SecondaryButton
                onClick={() => handleDeleteRecordClick(scopeType)}
                disabled={isDeleteDisabled || mappedFields.length === 0}
              >
                Delete record
              </SecondaryButton>
              <PrimaryButton onClick={addRow}>
                Add field
              </PrimaryButton>
            </div>
          )}
        </div>
      )}

      {/* Scope-level delete confirmation */}
      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete scope type</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{scopeTypeToDelete}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteRecord}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}

      {/* Field-level delete confirmation */}
      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete field mapping</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteFieldModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteField}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
